import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { isTravelAdminRole } from '@/module/Travel/travelRoles';

import { NoContentPlaceholder } from '@/core/components';

import { useCurrentUser } from '@feature/security';
import { useAppNotification } from '@feature/utils';

import { UniversityConfigForm } from './UniversityConfigForm';
import { getUniversityConfigOptions, useUniversityConfigUpdate } from './model/UniversityConfigQuery';
import {
  UniversityConfigSchema,
  defaultUniversityConfigValues,
  toUniversityConfigRequest,
  toUniversityConfigSchema,
  universityConfigSchema,
} from './model/UniversityConfigSchema';

export function UniversityConfigEdit() {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();

  if (!isTravelAdminRole(currentRole)) {
    return <NoContentPlaceholder title={t('universityConfig.noAccess')} />;
  }

  return <UniversityConfigEditContent />;
}

function UniversityConfigEditContent() {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const { data } = useSuspenseQuery(getUniversityConfigOptions());
  const { mutate, isPending, isError, isSuccess } = useUniversityConfigUpdate();

  const onSubmit = (values: UniversityConfigSchema) =>
    mutate(toUniversityConfigRequest(values), {
      onError: notification.error,
      onSuccess: notification.success,
    });

  const values = useMemo(() => toUniversityConfigSchema(data), [data]);

  return (
    <UniversityConfigForm
      title={t('universityConfig.title')}
      schema={universityConfigSchema}
      defaultValues={defaultUniversityConfigValues}
      values={values}
      isSubmitting={isPending}
      isError={isError}
      isSuccess={isSuccess}
      onSubmit={onSubmit}
      onCancel={undefined}
    />
  );
}
